import { SOCKET_EVENTS } from '@/api/socket-events';
import { socket } from '@/api/socket';
import type {
	ICardFillAdminCard,
	ICardFillAdminJob,
	ICardFillAdminSnapshot,
} from '@/entities/card-fill-admin.entity';
import type { CardFillAdminStats } from '@/api/admin';
import { useCallback, useEffect, useState } from 'react';

function upsertJob(list: ICardFillAdminJob[], job: ICardFillAdminJob): ICardFillAdminJob[] {
	const idx = list.findIndex(item => item.id === job.id);
	if (idx === -1) {
		return [job, ...list];
	}
	const next = [...list];
	next[idx] = job;
	return next;
}

function removeFromList(list: ICardFillAdminJob[], jobId: string): ICardFillAdminJob[] {
	return list.filter(item => item.id !== jobId);
}

function partitionJob(job: ICardFillAdminJob, state: ICardFillAdminSnapshot): ICardFillAdminSnapshot {
	let pending = removeFromList(state.pending, job.id);
	let running = removeFromList(state.running, job.id);
	let history = removeFromList(state.history, job.id);

	if (job.status === 'pending') {
		pending = upsertJob(pending, job);
	} else if (job.status === 'running') {
		running = upsertJob(running, job);
	} else {
		history = upsertJob(history, job);
	}

	return { ...state, pending, running, history };
}

export function useCardFillAdminSubscription() {
	const [snapshot, setSnapshot] = useState<ICardFillAdminSnapshot | null>(null);
	const [connected, setConnected] = useState(false);

	useEffect(() => {
		if (!socket) return;

		const onSnapshot = (data: ICardFillAdminSnapshot) => {
			setSnapshot(data);
			setConnected(true);
		};

		const onJob = (job: ICardFillAdminJob) => {
			setSnapshot(prev => (prev ? partitionJob(job, prev) : prev));
		};

		const onCard = (card: ICardFillAdminCard) => {
			setSnapshot(prev => {
				if (!prev) return prev;
				const cardIdx = prev.cards.findIndex(c => c.id === card.id);
				const nextCards =
					cardIdx === -1 ? [card, ...prev.cards] : prev.cards.map((c, i) => (i === cardIdx ? card : c));
				return { ...prev, cards: nextCards };
			});
		};

		const onStats = (stats: CardFillAdminStats) => {
			setSnapshot(prev => (prev ? { ...prev, stats } : prev));
		};

		const subscribe = () => {
			socket.emit(SOCKET_EVENTS.subscribeCardFillAdmin);
		};

		socket.on(SOCKET_EVENTS.cardFillAdminSnapshot, onSnapshot);
		socket.on(SOCKET_EVENTS.cardFillAdminJob, onJob);
		socket.on(SOCKET_EVENTS.cardFillAdminCard, onCard);
		socket.on(SOCKET_EVENTS.cardFillAdminStats, onStats);
		socket.on(SOCKET_EVENTS.connect, subscribe);

		if (socket.connected) {
			subscribe();
		}

		return () => {
			socket.off(SOCKET_EVENTS.cardFillAdminSnapshot, onSnapshot);
			socket.off(SOCKET_EVENTS.cardFillAdminJob, onJob);
			socket.off(SOCKET_EVENTS.cardFillAdminCard, onCard);
			socket.off(SOCKET_EVENTS.cardFillAdminStats, onStats);
			socket.off(SOCKET_EVENTS.connect, subscribe);
			socket.emit(SOCKET_EVENTS.unsubscribeCardFillAdmin);
			setConnected(false);
		};
	}, []);

	const cancelJob = useCallback((jobId: string) => {
		socket?.emit(SOCKET_EVENTS.cancelCardFillJob, { jobId });
	}, []);

	const retryJob = useCallback((jobId: string) => {
		socket?.emit(SOCKET_EVENTS.retryCardFillJob, { jobId });
	}, []);

	const appendHistory = useCallback((items: ICardFillAdminJob[], nextCursor: string | null) => {
		setSnapshot(prev => {
			if (!prev) return prev;
			const existing = new Set(prev.history.map(j => j.id));
			const merged = [...prev.history, ...items.filter(j => !existing.has(j.id))];
			return { ...prev, history: merged, historyCursor: nextCursor };
		});
	}, []);

	const appendCards = useCallback((items: ICardFillAdminCard[], nextCursor: string | null) => {
		setSnapshot(prev => {
			if (!prev) return prev;
			const existing = new Set(prev.cards.map(c => c.id));
			const merged = [...prev.cards, ...items.filter(c => !existing.has(c.id))];
			return { ...prev, cards: merged, cardsCursor: nextCursor };
		});
	}, []);

	return {
		snapshot,
		connected,
		cancelJob,
		retryJob,
		appendHistory,
		appendCards,
	};
}
